<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="apiSettingsForm"
            name="apiSettingsForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-md-6">
                <div class="form-group">
                    <!--Name-->
                    <x-input
                        id="apiSettingsName"
                        type="text"
                        :text="this.$t('apiSettings.nameTitle')"
                        required
                        v-model="apiSettings.name"
                        @input="$_onInputName"
                        :invalidMessage="nameMessage"
                        :state="!errorInUse"
                        :maxlength="50"
                        :autofocus="true"
                    >
                    </x-input>
                    <!-- choose api type -->
                    <b-form-group>
                        <label>
                            {{ $t('apiSettings.type') }}
                        </label>
                        <b-form-select
                            id="apiSettingsType"
                            v-model="apiSettings.apiType"
                            :options="apiSettingsTypeOptions"
                        >
                        </b-form-select>
                    </b-form-group>
                    <!--Base Url-->
                    <template v-if="showBaseUrlInput">
                        <base-input
                            class="mb-3"
                            id="apiSettingsBaseUrl"
                            type="text"
                            :text="this.$t('apiSettings.baseUrlTitle')"
                            required
                            v-model="apiSettings.baseUrl"
                        >
                        </base-input>
                        <x-checkbox
                            id="canOverruleBaseUrl"
                            name="canOverruleBaseUrl"
                            :state="null"
                            :plainMode="true"
                            v-model="apiSettings.canOverruleBaseUrl"
                            :expandClickArea="true"
                            :disabled="apiSettings.hasOrganizationApiWithUrl"
                            :text="$t('apiSettings.canOverruleBaseUrlTitle')"
                        >
                        </x-checkbox>
                    </template>
                    <div class="mt-3" v-if="showCertificateOption">
                        <x-checkbox
                            id="certificateIsRequired"
                            name="certificateIsRequired"
                            :state="null"
                            :plainMode="true"
                            v-model="apiSettings.certificateIsRequired"
                            :disabled="
                                apiSettings.hasOrganizationApiWithCertificate
                            "
                            :expandClickArea="true"
                            :text="$t('apiSettings.clientCertificate')"
                        >
                        </x-checkbox>
                    </div>
                </div>
                <!-- Save/cancel/remove buttons -->
                <SubmitCancelRemove
                    :is-saving="isSaving"
                    :is-deleting="isDeleting"
                    :mode="mode"
                    :isBtnSaveDisabled="isBtnSaveDisabled"
                    @cancel="$_onCancel"
                    @remove="$_onRemove"
                >
                </SubmitCancelRemove>
            </div>
        </b-form>
    </div>
</template>

<script>
    /* Store imports */
    import { mapState } from 'vuex';
    import { MODE_EDIT, MODE_ADD, API_URL_TYPE } from '../../constants';
    import { arraySortAscendingFunction } from '../../services/utilities';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import XCheckBox from '../../components/BaseComponents/x-checkbox.vue';
    import BaseInput from '../../components/BaseComponents/base-input.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';

    export default {
        name: 'ApiSettingsDetail',
        mixins: [modalMixin],

        components: {
            SubmitCancelRemove,
            'x-input': XInput,
            'x-checkbox': XCheckBox,
            'base-input': BaseInput
        },

        props: {
            // url connection license id
            id: { type: Number, default: -1 }
        },

        data: function () {
            return {
                mode: 0,
                title: '',
                apiSettings: {
                    id: -1,
                    name: '',
                    baseUrl: '',
                    apiType: 0
                },
                apiTypeSettings: [],
                // validation
                errorInUse: false,
                nameMessage: '',
                isSaving: false,
                isDeleting: false,
                // api types
                apiTypeLabels: []
            };
        },

        computed: {
            ...mapState(['userInfo']),

            showCertificateOption() {
                if (this.apiTypeSetting) {
                    // Show certificate checkbox when type has a certificate and this is optional
                    return (
                        this.apiTypeSetting.hasCertificate &&
                        this.apiTypeSetting.certificateIsOptional
                    );
                } else {
                    return false;
                }
            },

            showBaseUrlInput() {
                if (this.apiTypeSetting) {
                    // Show Base URL input if the API type has an url type setting of Base
                    return this.apiTypeSetting.urlType === API_URL_TYPE.BASE;
                } else {
                    return false;
                }
            },

            apiTypeSetting() {
                if (this.apiSettings && this.apiTypeSettings.length > 0) {
                    // Find the API type setting for the current type
                    let apiTypeSetting = this.apiTypeSettings.find(
                        (a) => a.apiType === this.apiSettings.apiType
                    );

                    return apiTypeSetting;
                } else {
                    return null;
                }
            },
            /**
             * Check if connection license can be saved
             * @returns {boolean} indicating if organization can be saved
             */
            isBtnSaveDisabled: function () {
                return this.isSaving || this.isDeleting;
            },

            apiSettingsTypeOptions() {
                return this.apiTypeLabels;
            }
        },

        async created() {
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('apiSettings.detailEditTitle');
                this.$_getApiSettings();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('apiSettings.detailAddTitle');
            }
            await this.$_getApiTypeSettings();
        },

        methods: {
            async $_getApiSettings() {
                try {
                    const apiSettingsResult = await this.$store.dispatch(
                        'useApiData',
                        { name: 'GetApi', parameters: { Id: this.id } }
                    );
                    this.apiSettings = Object.assign({}, apiSettingsResult.data);
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('apiSettings.errorGetApiSettings'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            async $_getApiTypeSettings() {
                try {
                    const apiSettingsResult = await this.$store.dispatch(
                        'useApiData',
                        { name: 'GetApiTypeSettings' }
                    );
                    // Only use available API types.
                    // This is a value in the database to block usage of an API type in production when
                    // code is not yet fully functional.
                    // Examples are WOZ and BRP per 01-01-2024

                    this.apiTypeSettings = apiSettingsResult.data;

                    this.apiTypeSettings
                        .filter((s) => s.available)
                        .forEach((item) => {
                            let localeEntry = this.$t(
                                `apiSettings.${item.apiName.toLowerCase()}`
                            );
                            this.apiTypeLabels.push({
                                value: item.apiType,
                                text: localeEntry
                            });
                        });
                    this.apiTypeLabels.sort(arraySortAscendingFunction('text'));
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('apiSettings.errorGetApiTypeSettings'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * check API setting existance
             */
            $_onInputName(newValue) {
                this.errorInUse = false;
            },

            $_goToOverview() {
                this.$router.push({ path: '/dashboard/apiSettings' });
            },

            $_onCancel: function () {
                this.$_goToOverview();
            },

            /*
             * Removes the connection license
             */
            $_onRemove() {
                if (!this.userInfo.isSuper) {
                    this.msgBoxOk({
                        message: this.$t('noDeletePermittedNoSuperUser')
                    });
                    return;
                }
                const confirmMessage = {
                    message: this.$t('apiSettings.deleteConfirmation')
                };

                this.msgBoxConfirm(confirmMessage).then(async (confirm) => {
                    if (confirm) {
                        this.isDeleting = true;
                        try {
                            await this.$store.dispatch('useApiData', {
                                name: 'DeleteApi',
                                parameters: { Id: this.id }
                            });
                            this.isDeleting = false;
                            const data = {
                                message: this.$t('apiSettings.removed'),
                                variant: 'success',
                                autoHideDelay: 5000
                            };
                            this.showToast(data);
                            this.$_goToOverview();
                        } catch (error) {
                            const data = {
                                message: this.$t(error.message),
                                variant: 'danger',
                                title: this.$t(error.title),
                                autoHideDelay: 5000
                            };
                            this.showToast(data);
                            this.isDeleting = false;
                        }
                    }
                });
            },

            async $_onSave() {
                this.isSaving = true;
                this.nameMessage = '';

                const data = this.apiSettings;
                const parameters = data.id > -1 ? { Id: this.id } : undefined;
                const name = data.id > -1 ? 'UpdateApi' : 'CreateApi';
                try {
                    await this.$store.dispatch('useApiData', {
                        name,
                        parameters,
                        data
                    });
                    this.isSaving = false;
                    const messageData = {
                        message: this.$t('apiSettings.saved'),
                        variant: 'success',
                        autoHideDelay: 5000
                    };
                    this.showToast(messageData);
                    this.$_goToOverview();
                } catch (error) {
                    const statusCode = error.response.status;
                    let messageData;
                    if (statusCode === 409) {
                        this.nameMessage = this.$t('apiSettings.nameIsInUse');
                        this.errorInUse = true;
                        messageData = {
                            message: this.$t('apiSettings.nameIsInUse'),
                            variant: 'danger',
                            title: this.$t(error.title),
                            autoHideDelay: 5000
                        };
                    } else {
                        messageData = {
                            message: this.$t(error.message),
                            variant: 'danger',
                            title: this.$t(error.title),
                            autoHideDelay: 5000
                        };
                    }
                    console.log('---', error.response.status);
                    this.showToast(messageData);
                    this.isSaving = false;
                }
            },

            $_onSubmit() {
                if (this.$refs.apiSettingsForm.checkValidity()) {
                    this.$_onSave();
                }
            }
        }
    };
</script>
